/**
 * annuityStrategies.ts
 * Centralises all calculations related to annuity-based strategies.
 * Ported verbatim from frontend/src/calculations/AnnuityStrategies.js.
 */
import { ASSET_TYPES } from './constants/assetTypes.ts';
import type { FactFind } from './factFindTypes.ts';
import {
    lifetimeAnnuityRate,
    guaranteedAnnuityRate,
    fixedTermAnnuityRate,
    annuityRateSheet,
    type AnnuityRateAnswer,
} from './annuityRates.ts';
import { carePlusSplitFor } from './carePlusSplit.ts';

export { annuityRateSheet, lifetimeAnnuityRate, guaranteedAnnuityRate, fixedTermAnnuityRate };
export type { AnnuityRateAnswer };

/**
 * AUSTRALIAN LIFE TABLES 2020-22 — Australian Government Actuary, released 13 December 2024.
 * Complete expectation of life (e_x) by single age and sex.
 *
 * THIS IS THE TABLE THE RULES ARE MEASURED FROM. Social security applies the AGA tables in force at
 * an income stream's commencement: this edition for commencements on or after 1 January 2025 (the
 * 2015-17 edition before that). Two things read it:
 *   - the CAPITAL ACCESS SCHEDULE for a lifetime income stream (SIS reg 1.06A): the "life expectancy
 *     period" is the owner's expectation of life at commencement, the LONGER of the two where the
 *     income continues to a partner, rounded DOWN to whole years;
 *   - the ATO's deductible amount for a lifetime annuity's tax-free component.
 * The table it replaces (25 Sep 2026) had no recorded source — it was carried across from the
 * original spreadsheet and flagged as an open item for the reviewer in the pack's v1.17 entry.
 * Transcribed from the
 * published table on 25 September 2026; ages below 55 are not needed here. TO VERIFY for the actuary:
 * against the AGA publication itself (aga.gov.au/publications/life-tables/australian-life-tables-2020-22).
 */
export const AGA_LIFE_TABLE_2020_22: Record<number, { male: number; female: number }> = {
    55: { male: 28.82, female: 31.92 }, 56: { male: 27.93, female: 31.00 }, 57: { male: 27.06, female: 30.08 },
    58: { male: 26.19, female: 29.17 }, 59: { male: 25.32, female: 28.26 }, 60: { male: 24.47, female: 27.35 },
    61: { male: 23.62, female: 26.45 }, 62: { male: 22.78, female: 25.56 }, 63: { male: 21.94, female: 24.67 },
    64: { male: 21.12, female: 23.78 }, 65: { male: 20.30, female: 22.90 }, 66: { male: 19.48, female: 22.02 },
    67: { male: 18.68, female: 21.15 }, 68: { male: 17.89, female: 20.29 }, 69: { male: 17.10, female: 19.43 },
    70: { male: 16.32, female: 18.59 }, 71: { male: 15.56, female: 17.75 }, 72: { male: 14.80, female: 16.92 },
    73: { male: 14.06, female: 16.10 }, 74: { male: 13.33, female: 15.30 }, 75: { male: 12.62, female: 14.50 },
    76: { male: 11.92, female: 13.72 }, 77: { male: 11.24, female: 12.96 }, 78: { male: 10.58, female: 12.21 },
    79: { male: 9.94, female: 11.48 }, 80: { male: 9.32, female: 10.77 }, 81: { male: 8.72, female: 10.08 },
    82: { male: 8.14, female: 9.42 }, 83: { male: 7.59, female: 8.78 }, 84: { male: 7.06, female: 8.16 },
    85: { male: 6.55, female: 7.57 }, 86: { male: 6.08, female: 7.01 }, 87: { male: 5.63, female: 6.48 },
    88: { male: 5.21, female: 5.98 }, 89: { male: 4.83, female: 5.51 }, 90: { male: 4.48, female: 5.09 },
    91: { male: 4.16, female: 4.69 }, 92: { male: 3.88, female: 4.33 }, 93: { male: 3.62, female: 4.00 },
    94: { male: 3.39, female: 3.71 }, 95: { male: 3.18, female: 3.44 }, 96: { male: 2.99, female: 3.20 },
    97: { male: 2.81, female: 2.99 }, 98: { male: 2.65, female: 2.80 }, 99: { male: 2.50, female: 2.63 },
    100: { male: 2.37, female: 2.49 }, 101: { male: 2.25, female: 2.36 }, 102: { male: 2.15, female: 2.24 },
    103: { male: 2.06, female: 2.14 }, 104: { male: 1.98, female: 2.04 }, 105: { male: 1.90, female: 1.96 },
};

export type Sex = 'male' | 'female';

/** A recorded sex, or null. Anything else on the record reads as unknown. */
export const sexOf = (v: unknown): Sex | null => (v === 'male' || v === 'female' ? v : null);

/**
 * Expectation of life at `age`. With the sex unknown the SHORTER figure (male) is used: it makes the
 * capital access schedule shorter, so the model never credits a capital-returned annuity with more
 * coming back than the rules would allow for either sex. The screen says when this fallback is in play.
 */
export function lifeExpectancyAt(age: number, sex: Sex | null | undefined): number {
    const tableAge = clamp(Math.round(age), 55, 105);
    const row = AGA_LIFE_TABLE_2020_22[tableAge];
    if (!row) return 15;
    return sex === 'female' ? row.female : row.male;
}

/*
 * ANNUITY PRICES LIVE IN `annuityRates.ts`, READ FROM THE PUBLISHED SHEET.
 *
 * What stood here until 25 September 2026 was a `DEFAULT_RATE_RULES` table: a base rate, a flat age
 * step and three discounts, all invented. Measured against Challenger's published adviser sheet it
 * paid about HALF the market at every age and in both shapes, so every annuity strategy in every
 * module was understated. The rules also carried a `care` block that nothing could ever reach,
 * because no caller has ever set `annuityType` to `'care'`.
 *
 * Rates are a price, not a constant, so they are now data imported weekly from the sheet. See
 * `annuityRates.ts` for which published column maps to which client-facing shape, and
 * `scripts/import-annuity-rates.mjs` for the update drill.
 */

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export function getLifeExpectancy({
    age,
    partnerAge = null,
    type = 'single',
    sex = null,
    partnerSex = null,
}: {
    age: number;
    partnerAge?: number | null;
    type?: string;
    sex?: Sex | null;
    partnerSex?: Sex | null;
}): number {
    // A joint or reversionary annuity is planned on the LONGER of the two lives — the rule the capital
    // access schedule applies where the income continues to a partner.
    const own = lifeExpectancyAt(age, sex);
    if (type === 'joint' && partnerAge !== null && partnerAge !== undefined) {
        return Math.max(own, lifeExpectancyAt(partnerAge, partnerSex));
    }
    return own;
}

/*
 * THE CAPITAL ACCESS SCHEDULE (SIS reg 1.06A), the reason a "capital returned" lifetime annuity is
 * not a promise of the purchase price for life.
 *
 * Centrelink counts a lifetime annuity at 60% of its purchase price (then 30%) ONLY while its
 * death benefit stays inside this schedule: the full purchase price may be paid on death during the
 * FIRST HALF of the owner's life expectancy period, after which the maximum falls in a straight line
 * to nothing at the end of that period. A product paying 100% for life would be assessed at the
 * greater of 60% and its death benefit — in effect the full price — and none is sold as a
 * means-tested annuity for that reason (Challenger's CarePlus pairs a schedule-compliant annuity
 * with a separate life policy, counted in full, to get there).
 *
 * Until 25 September 2026 this engine held a capital-returned annuity's residual at 100% of the
 * purchase price for the whole projection AND assessed it at 60% — the best of both worlds, and not
 * a product anyone can buy (owner-caught, via Challenger's CarePlus material). The residual now
 * follows the schedule; the 60%/30% assessment is unchanged because every modelled annuity is a
 * compliant one. The life expectancy period is the owner's expectation of life at commencement
 * (the longer of the two where the income continues to a partner), rounded down to whole years.
 */
export function capitalAccessPeriodYears(lifeExpectancyYears: number): number {
    return Math.max(1, Math.floor(lifeExpectancyYears));
}

/**
 * The share of the purchase price the schedule still allows to be TAKEN BACK, `yearsElapsed` in.
 *
 * This is the other half of SIS reg 1.06A, and until now the app ignored it. The schedule caps two
 * different things, and they run on different lines: the DEATH benefit may stay at the full purchase
 * price for the first half of the period (above), while the maximum a living owner may commute falls
 * in a straight line from the whole price at the start to nothing at the end. So three years into a
 * six-year period a client's estate would receive the full amount, but the client themselves could
 * take back at most half.
 *
 * WHY IT MATTERS ON SCREEN. Several screens told the client the money was "locked in" and that they
 * "cannot change your mind later and ask for the capital back". That is true of a nil-residual
 * annuity, where there is no withdrawal value at all, and wrong of a capital-returned one, which has
 * a declining one for years. The question "what if I need it back?" is the one a client in care asks
 * first, so the figure is now shown beside the income and the death benefit.
 *
 * IT IS A MAXIMUM, NOT A QUOTE. The rules cap what a provider MAY pay; what it WOULD pay depends on
 * interest rates on the day and the cost of breaking the investment, so the real figure is usually
 * lower. Nothing in the projection ever spends this: no plan should assume an annuity is broken.
 */
export function capitalAccessWithdrawalFactor(yearsElapsed: number, periodYears: number): number {
    const period = Math.max(0, periodYears);
    const elapsed = Math.max(0, yearsElapsed);
    if (period <= 0 || elapsed >= period) return 0;
    return Math.max(0, Math.min(1, (period - elapsed) / period));
}

/**
 * What the estate actually receives on death, `yearsElapsed` in, as a share of the death benefit
 * chosen at purchase.
 *
 * THE MARKET'S SHAPE, NOT THE REGULATORY CEILING (corrected 25 Sep 2026, owner-caught). SIS reg
 * 1.06A sets a MAXIMUM: it permits the full amount for the first half of the life expectancy period
 * and then a straight line to nil at the end of it. Until now this function paid that ceiling, which
 * put the estate at two-thirds of the purchase price four years into a six-year period.
 *
 * Challenger's Liquid Lifetime PDS — the market's main product — states the shape they actually
 * sell, and it is lower:
 *
 *     "For half of your withdrawal period (rounded down to a whole day), there is a money back
 *      guarantee. During this period we will pay a death benefit equal to 100% of the amount
 *      invested... For the remainder of the withdrawal period, we will pay a death benefit equal to
 *      THE MAXIMUM WITHDRAWAL AMOUNT. We do not pay anything if you die outside the withdrawal
 *      period."
 *
 * So after the halfway point the death benefit is not a fresh line down to nil — it simply becomes
 * the withdrawal value, which has been falling from the whole price since day one. Four years into a
 * six-year period that is a third, not two-thirds. There is a genuine step down at the halfway
 * point, from 100% to 50%, and it is in the product, not an artefact here.
 *
 * WHY WE FOLLOW CHALLENGER RATHER THAN THE CEILING. The owner's reasoning, and it is right: the
 * ceiling is what a provider MAY pay, not what any provider DOES; showing a family more coming back
 * than the market would actually pay is the error that gets an adviser sued. It is also the
 * conservative direction for every estate figure in the app. Paying less than the ceiling keeps the
 * annuity fully compliant, so the 60%/30% assets-test treatment is unaffected.
 *
 * A HAPPY CONSEQUENCE FOR THE SCREENS: past the halfway point the death benefit and the cash-out
 * value are now the SAME number, so the client has one line to follow after that point instead of
 * two.
 *
 * I got this wrong in both directions before landing here. First I proposed Challenger's shape from
 * a misreading of their CarePlus annuity (whose death benefit is deliberately stripped, because the
 * paired insurance covers it), then retracted the proposal as unfounded. The PDS wording above is
 * the authority, and it says what the owner said it said.
 */
export function capitalAccessDeathBenefitFactor(yearsElapsed: number, periodYears: number): number {
    const period = Math.max(0, periodYears);
    const elapsed = Math.max(0, yearsElapsed);
    if (period <= 0 || elapsed >= period) return 0;
    if (elapsed < period / 2) return 1;
    return capitalAccessWithdrawalFactor(elapsed, period);
}

/**
 * What the provider would pay, from the published sheet.
 *
 * A LIFETIME annuity gets a payment rate straight from the sheet's own two liquidity options. A
 * FIXED TERM gets an EARNING rate, which `buildAnnuityImpact` then amortises into a payment, because
 * a term annuity hands capital back through its payments. A GUARANTEED shape gets the rate for the
 * annuity-and-insurance product the sheet prices as one.
 *
 * The answer carries `extrapolated` so the screens can say when the sheet does not publish the
 * client's age and we have carried its last slope onward. Nothing here invents a rate.
 */
function estimateIndicativeRate({
    annuityType,
    age,
    partnerAge,
    indexation,
    residualCapital,
    termYears,
    sex,
}: {
    annuityType: string;
    age: number;
    partnerAge: number | null;
    indexation: boolean;
    residualCapital: number;
    termYears: number | null;
    sex?: 'male' | 'female' | null;
}): AnnuityRateAnswer {
    if (annuityType === 'fixedTerm') {
        return fixedTermAnnuityRate({ termYears: termYears || 1, residualCapital });
    }
    if (annuityType === 'guaranteed') {
        return guaranteedAnnuityRate({ age, sex });
    }
    return lifetimeAnnuityRate({ age, sex, indexation, residualCapital, partnerAge });
}

function computeAnnualPaymentWithIndexation(purchaseAmount: number, rate: number, options: { indexation: boolean; annuityType: string }): number {
    if (!options.indexation || options.annuityType === 'fixedTerm') {
        return purchaseAmount * rate;
    }
    // With indexation we assume the rate represents the first year's payment.
    return purchaseAmount * rate;
}

function calculateDeductionAmount({
    purchaseAmount,
    residualCapital,
    lifeExpectancyYears,
    termYears,
    annuityType,
}: {
    purchaseAmount: number;
    residualCapital: number;
    lifeExpectancyYears: number | null;
    termYears: number | null;
    annuityType: string;
}): number {
    const residualValue = residualCapital ? purchaseAmount * residualCapital : 0;
    const capitalToReturn = purchaseAmount - residualValue;

    if (annuityType === 'fixedTerm' && termYears) {
        return capitalToReturn / termYears;
    }
    const divisor = lifeExpectancyYears || 1;
    return capitalToReturn / divisor;
}

/**
 * Calculates the assessable asset for Centrelink purposes.
 * For lifetime annuities commenced on or after 1 July 2019:
 *   Asset test: 60% of purchase price until age 85, then 30% (regardless of RCV).
 * For fixed-term annuities: RCV=100% keeps full purchase price; RCV<100% reduces over time.
 * For joint ownership, each partner's share is assessed based on their own age.
 */
/**
 * The assets-test rates for a lifetime income stream, and the day the first becomes the second.
 *
 * THE RULE. 60% of the purchase price is assessed until the "threshold day", then 30% for life. The
 * threshold day is the LATER of the person reaching the threshold age and five years from
 * commencement — so the minimum period at 60% is five years, for everyone, however old they are
 * when they buy.
 *
 * THE AGE MOVED. It is 85 for income streams commencing on or after 1 January 2025 (updated
 * Australian Life Tables) and 84 for those commenced 1 July 2019 to 31 December 2024. This models a
 * purchase made NOW, so 85 is the applicable figure. A client who already HOLDS a pre-2025 annuity
 * keeps a threshold of 84, which this cannot currently express — the fact-find has no commencement
 * date for an existing annuity to switch on. Worth knowing before reading a projection for one.
 */
export const LIFETIME_ANNUITY_ASSET_RATE_BEFORE_THRESHOLD = 0.6;
export const LIFETIME_ANNUITY_ASSET_RATE_AFTER_THRESHOLD = 0.3;
export const LIFETIME_ANNUITY_THRESHOLD_AGE = 85;
/** The floor under the 60% period. Five years from commencement, whatever the age at purchase. */
export const LIFETIME_ANNUITY_MINIMUM_YEARS_BEFORE_THRESHOLD = 5;

/**
 * Which assets-test rate applies, given the owner's age when they bought and how long they have
 * held it.
 *
 * THE BUG THIS REPLACES (2026-08-28). This was `ownerAge >= 85 ? 0.30 : 0.60`, read once at
 * purchase — a bare age check with no five-year minimum. So a client buying at 86 was assessed at
 * 30% from day one, when the rule holds them at 60% until 91. On a $300,000 annuity that is $90,000
 * too little assessed, every year: a lower means-tested care fee and a higher Age Pension than they
 * would actually receive. It FLATTERED the strategy, for an age band that is common in residential
 * care — which is the direction that matters, because a family could commit capital for life on the
 * strength of a benefit that does not arrive for five years.
 *
 * Because the minimum is five years, the answer at commencement is ALWAYS 60% — nobody can be at
 * 30% on the day they buy. `yearsElapsed` is honoured, and since 17 September 2026 the projection
 * ages every annuity at the opening of each year (plugins/annuityPlugin.ts), so the step to 30% is
 * applied on the threshold day partway through a run and written into that year's narrative
 * (annuityAgeing.test.ts pins it). A client who is 85 when they buy is therefore at 60% for five
 * years and at 30% from the sixth — the answer to "why is my 85-year-old still at 60%?" (owner,
 * 23 Sep 2026): the five-year floor, not the age, governs a purchase made in care.
 */
export function lifetimeAnnuityAssetRate(ageAtCommencement: number | null | undefined, yearsElapsed = 0): number {
    // An unknown age cannot be shown to have passed the threshold, so it gets the higher rate. The
    // cautious direction: it never claims a discount the client may not have reached.
    if (ageAtCommencement == null || !(ageAtCommencement > 0)) return LIFETIME_ANNUITY_ASSET_RATE_BEFORE_THRESHOLD;

    const yearsToThresholdAge = Math.max(0, LIFETIME_ANNUITY_THRESHOLD_AGE - ageAtCommencement);
    const yearsToThresholdDay = Math.max(yearsToThresholdAge, LIFETIME_ANNUITY_MINIMUM_YEARS_BEFORE_THRESHOLD);

    return (yearsElapsed || 0) >= yearsToThresholdDay
        ? LIFETIME_ANNUITY_ASSET_RATE_AFTER_THRESHOLD
        : LIFETIME_ANNUITY_ASSET_RATE_BEFORE_THRESHOLD;
}

function deriveAssessableAsset({
    annuityType,
    purchaseAmount,
    residualCapital = 0,
    deductionAmount = 0,
    yearsElapsed = 0,
    age,
    partnerAge = null,
    owner = 'client',
    ownerSex = null,
}: {
    annuityType: string;
    purchaseAmount: number;
    residualCapital?: number;
    deductionAmount?: number;
    yearsElapsed?: number;
    age: number;
    partnerAge?: number | null;
    owner?: string;
    /** Only the guaranteed shape reads this: the split table is by sex. */
    ownerSex?: Sex | null;
}): number {
    if (annuityType === 'fixedTerm') {
        const residualValue = purchaseAmount * (residualCapital || 0);

        // If 100% RCV, full purchase price assessed throughout term
        if (residualCapital >= 1) {
            return purchaseAmount;
        }

        // If RCV < 100%, asset reduces over time: Max(Residual Value, Purchase Price - (Deduction × Years))
        if (deductionAmount > 0 && yearsElapsed > 0) {
            const capitalReturned = deductionAmount * yearsElapsed;
            return Math.max(residualValue, purchaseAmount - capitalReturned);
        }

        // Initial assessment (yearsElapsed = 0): Full purchase price
        return purchaseAmount;
    }

    /*
     * THE GUARANTEED SHAPE IS TWO PRODUCTS, COUNTED TWO WAYS. The annuity slice is a compliant
     * lifetime income stream, so it gets the 60% then 30% treatment on ITS price. The life policy
     * is not an income stream at all: Centrelink counts it at the greater of the premium paid and
     * its surrender value. The surrender value starts below the premium (it is the present value of
     * a benefit that has not fallen due) and can climb above it in later years; we count the
     * premium, which is the floor Challenger's own calculator applies, and record in the pack that
     * the true figure can be higher late in life (D.2.12b). Net effect, and worth knowing: the
     * guaranteed shape cuts assessed assets by only about 40% of the annuity slice — about 21% of
     * the money at 70, 12% at 85, 7% at 95 — because most of the money is buying insurance.
     */
    if (annuityType === 'guaranteed') {
        const split = carePlusSplitFor(owner === 'partner' ? (partnerAge ?? age) : age, ownerSex);
        const annuitySlice = purchaseAmount * split.annuity;
        const insuranceSlice = purchaseAmount * split.insurance;
        const ownerAgeForRate = owner === 'partner' ? partnerAge : age;
        return annuitySlice * lifetimeAnnuityAssetRate(ownerAgeForRate, yearsElapsed) + insuranceSlice;
    }

    // Lifetime annuities: 60% of the purchase price until the threshold day, then 30%.
    if (owner === 'joint' && partnerAge !== null) {
        const clientShare = purchaseAmount / 2;
        const partnerShare = purchaseAmount / 2;
        return (
            clientShare * lifetimeAnnuityAssetRate(age, yearsElapsed)
            + partnerShare * lifetimeAnnuityAssetRate(partnerAge, yearsElapsed)
        );
    }

    const ownerAge = owner === 'partner' ? partnerAge : age;
    return purchaseAmount * lifetimeAnnuityAssetRate(ownerAge, yearsElapsed);
}

export function buildAnnuityImpact(factFind: FactFind, strategy: any): any {
    const {
        annuityType,
        purchaseAmount,
        fundingSource,
        age,
        partnerAge = null,
        indexation = false,
        residualCapital = 0,
        termYears = null,
        incomeOverride = null,
        assessableIncomeOverride = null,
        assessableAssetOverride = null,
        owner = 'client',
        reversionary = false,
        guaranteePeriodYears = 0,
        reversionPct = null,
    } = strategy;
    // A deferred lifetime annuity: payments start `deferralYears` after purchase. Only a lifetime
    // product defers; a fixed term with a deferral is not a product anyone sells.
    const deferralYears = annuityType === 'lifetime' ? Math.max(0, Math.round(Number(strategy.deferralYears) || 0)) : 0;

    if (!purchaseAmount || purchaseAmount <= 0) {
        return { warnings: ['Enter an annuity purchase amount to see projections.'] };
    }

    // Whose life the schedule runs on: the owner's, or the longer of the two where the income continues
    // to the partner (joint, or reversionary). Sex from the fact-find; unknown reads as the shorter table.
    // It is read BEFORE the rate call because the published sheet prices men and women differently.
    const ownerSex = sexOf(owner === 'partner' ? factFind?.partner?.sex : factFind?.client?.sex);
    const otherSex = sexOf(owner === 'partner' ? factFind?.client?.sex : factFind?.partner?.sex);
    const jointLife = owner === 'joint' || reversionary === true;

    // Priced at the age the payments START, on capital the provider is assumed to have grown through
    // the deferral (DEFERRED_ANNUITY_ACCUMULATION_RATE) — indicative, and low, see that constant.
    const rateAnswer = estimateIndicativeRate({
        annuityType,
        age: age + deferralYears,
        partnerAge: jointLife && partnerAge != null ? partnerAge + deferralYears : null,
        indexation,
        residualCapital,
        termYears,
        sex: ownerSex,
    });
    const indicativeRate = rateAnswer.rate;
    const deferralUplift = deferralYears > 0 ? Math.pow(1 + DEFERRED_ANNUITY_ACCUMULATION_RATE, deferralYears) : 1;

    let annualPaymentIndicative = computeAnnualPaymentWithIndexation(purchaseAmount, indicativeRate * deferralUplift, {
        indexation,
        annuityType,
    });
    /*
     * A FIXED TERM RETURNS ITS CAPITAL THROUGH THE PAYMENTS. The indicative rate is an earning rate
     * on the capital; the payment on a term annuity is that earning PLUS the part of the capital that
     * is not coming back at the end, spread over the term (an amortising payment). With the capital
     * fully returned (RCV 100%) the payment is the earning alone, as before; with none returned the
     * payment amortises the whole purchase price. Until 17 Sep 2026 every fixed term was priced at
     * the earning alone, so a nil-residual term annuity paid LESS than the capital it was supposed
     * to hand back each year — an impossible product, and one whose income-test deduction then
     * exceeded its payment.
     */
    if (annuityType === 'fixedTerm' && termYears && termYears > 0) {
        const residualPart = purchaseAmount * Math.min(1, Math.max(0, residualCapital || 0));
        const amortised = purchaseAmount - residualPart;
        const r = indicativeRate;
        const amortisingPayment = r > 0 ? (amortised * r) / (1 - Math.pow(1 + r, -termYears)) : amortised / termYears;
        annualPaymentIndicative = amortisingPayment + residualPart * r;
    }

    const lifeExpectancyYears =
        annuityType === 'fixedTerm'
            ? termYears
            : getLifeExpectancy({ age, partnerAge: jointLife ? partnerAge : null, type: jointLife && partnerAge !== null ? 'joint' : 'single', sex: ownerSex, partnerSex: otherSex });
    const capitalAccessPeriod = annuityType === 'lifetime' && lifeExpectancyYears ? capitalAccessPeriodYears(lifeExpectancyYears) : null;

    const deductionAmount = calculateDeductionAmount({
        purchaseAmount,
        residualCapital,
        lifeExpectancyYears,
        termYears,
        annuityType,
    });

    // For lifetime annuities commenced on or after 1 July 2019: income test = 60% of payments (no deduction).
    // The guaranteed shape's payments all come from its annuity half, so the same 60% applies.
    let assessableIncomeCalculated: number;
    if (annuityType === 'lifetime' || annuityType === 'guaranteed') {
        assessableIncomeCalculated = annualPaymentIndicative * 0.60;
    } else if (annuityType === 'fixedTerm') {
        // Term ≤ 5 years: subject to deeming (simplified here via deduction method, noted in UI).
        // Term > 5 years: deduction method applies.
        assessableIncomeCalculated = annualPaymentIndicative - deductionAmount;
    } else {
        assessableIncomeCalculated = annualPaymentIndicative - deductionAmount;
    }

    const assessableAssetCalculated = deriveAssessableAsset({
        annuityType,
        purchaseAmount,
        residualCapital,
        deductionAmount,
        yearsElapsed: 0, // Initial assessment
        age,
        partnerAge,
        owner,
        ownerSex,
    });

    const annualPayment = incomeOverride !== null && !Number.isNaN(incomeOverride) ? incomeOverride : annualPaymentIndicative;

    const assessableIncome =
        assessableIncomeOverride !== null && !Number.isNaN(assessableIncomeOverride) ? assessableIncomeOverride : assessableIncomeCalculated;

    const assessableAsset =
        assessableAssetOverride !== null && !Number.isNaN(assessableAssetOverride) ? assessableAssetOverride : assessableAssetCalculated;

    /*
     * WHAT THE ESTATE GETS. A plain annuity: the residual the client chose, which `ageAnnuityAsset`
     * then winds down on the capital access schedule. The guaranteed shape: the whole purchase
     * price, always — the life policy pays whatever the annuity does not, and nothing here or in
     * the ageing ever reduces it. That is the entire point of the product.
     */
    const isGuaranteed = annuityType === 'guaranteed';
    const residualValue = isGuaranteed ? purchaseAmount : (residualCapital ? purchaseAmount * residualCapital : 0);
    const split = isGuaranteed ? carePlusSplitFor(age, ownerSex) : null;

    let fundingSourceType = 'bank';
    let fundingSourceId = null;
    let fundingSourceLabel = 'Bank accounts / cash';

    if (typeof fundingSource === 'string') {
        fundingSourceType = fundingSource;
    } else if (fundingSource && typeof fundingSource === 'object') {
        fundingSourceType = fundingSource.type || fundingSourceType;
        fundingSourceId = fundingSource.assetId ?? null;
        if (fundingSource.label) {
            fundingSourceLabel = fundingSource.label;
        }
    }

    const annuityRecord: Record<string, any> = {
        id: strategy.id || `annuity-${Date.now()}`,
        annuityType,
        purchaseAmount,
        fundingSource,
        fundingSourceType,
        fundingSourceId,
        fundingSourceLabel,
        age,
        partnerAge,
        indexation,
        // A guaranteed shape returns everything; the field is kept at 1 so every reader that
        // scales by it (net worth, the recap, the cards) sees the full amount.
        residualCapital: isGuaranteed ? 1 : residualCapital,
        termYears,
        owner,
        reversionary,
        indicativeRate,
        /** Where the rate came from, in a sentence a client can read, and whether we had to go past
         *  the ages the sheet publishes. The screens print both. */
        rateBasis: rateAnswer.basis,
        rateExtrapolated: rateAnswer.extrapolated,
        rateSheetWeek: annuityRateSheet().effectiveFrom,
        annualPayment: roundCurrency(annualPayment),
        assessableIncome: roundCurrency(Math.max(0, assessableIncome)),
        assessableAsset: roundCurrency(Math.max(0, assessableAsset)),
        deductionAmount: roundCurrency(Math.max(0, deductionAmount)),
        residualValue: roundCurrency(Math.max(0, residualValue)),
        deferralYears,
        // A guarantee period and a spouse's share, where the shape has them (15 Oct 2026).
        guaranteePeriodYears: annuityType === 'lifetime' ? Math.max(0, Number(guaranteePeriodYears) || 0) : 0,
        reversionPct: reversionPct != null && Number.isFinite(Number(reversionPct)) ? Number(reversionPct) : null,
        // The capital access schedule this annuity's death benefit follows (lifetime only). The
        // guaranteed shape has no schedule to follow: its death benefit never moves.
        lifeExpectancyYears: annuityType === 'lifetime' ? lifeExpectancyYears : null,
        capitalAccessPeriodYears: capitalAccessPeriod,
        ownerSex,
        lifeExpectancySexKnown: annuityType === 'lifetime' ? (jointLife && partnerAge !== null ? ownerSex != null && otherSex != null : ownerSex != null) : true,
        /*
         * THE GUARANTEED SHAPE'S TWO HALVES, in dollars, so the screens can show the client where
         * their money went and the means test can count each half its own way. From Challenger's
         * calculator, by age and sex; `splitClamped` says the age fell outside the table.
         */
        ...(split ? {
            annuitySlice: roundCurrency(purchaseAmount * split.annuity),
            insuranceSlice: roundCurrency(purchaseAmount * split.insurance),
            splitClamped: split.clamped,
        } : {}),
    };

    if (deferralYears > 0) {
        // Nothing is paid, and nothing counted as income, until the deferral ends. The figures the
        // payments will take from then on ride along so the ageing plugin can switch them in.
        annuityRecord.deferredAnnualPayment = annuityRecord.annualPayment;
        annuityRecord.deferredAssessableIncome = annuityRecord.assessableIncome;
        annuityRecord.annualPayment = 0;
        annuityRecord.assessableIncome = 0;
    }

    return annuityRecord;
}

export function applyAnnuityToFactFind(factFind: FactFind, annuityRecord: any): any {
    const modified = structuredClone(factFind);

    const { fundingSource, fundingSourceType, fundingSourceId, purchaseAmount } = annuityRecord;
    const amount = purchaseAmount;
    let remaining = amount;

    const reduceFromList = (list: any[] | undefined, assetId: any, accessor = 'value') => {
        if (!list) return remaining;
        const targetId = assetId !== null && assetId !== undefined ? String(assetId) : null;
        list.some((item) => {
            if (remaining <= 0) return true;
            if (targetId && String(item.id ?? item.assetId ?? item.accountId) !== targetId) {
                return false;
            }
            const currentRaw = item[accessor] ?? item.balance;
            const currentValue = currentRaw || 0;
            if (currentValue <= 0) return !!targetId;
            const reduction = Math.min(currentValue, remaining);
            const updated = currentValue - reduction;
            if (item[accessor] !== undefined) {
                item[accessor] = updated;
            } else if (item.balance !== undefined) {
                item.balance = updated;
            }
            remaining -= reduction;
            return !!targetId;
        });
        return remaining;
    };

    let sourceType = fundingSourceType || (typeof fundingSource === 'object' ? fundingSource.type : fundingSource);
    let targetAssetId = fundingSourceId;

    // Handle asset ID format (e.g., "bankAccounts-123") from Aged Care section
    if (typeof fundingSource === 'object' && fundingSource.id && fundingSource.id.includes('-')) {
        const parts = fundingSource.id.split('-');
        const assetTypeFromId = parts[0];
        targetAssetId = parts.slice(1).join('-'); // Rejoin in case asset.id contains dashes

        if (assetTypeFromId === 'bankAccounts') {
            sourceType = 'bank';
        } else if (assetTypeFromId === 'accountBasedPensions') {
            sourceType = 'abp';
        } else if (assetTypeFromId === 'superannuation') {
            sourceType = 'super';
        } else {
            sourceType = assetTypeFromId;
        }
    }

    if (sourceType === 'bank' || sourceType === 'bankAccounts') {
        remaining = reduceFromList(modified.assets?.bankAccounts, targetAssetId, 'value');
    } else if (sourceType === 'shares') {
        remaining = reduceFromList(modified.assets?.shares, targetAssetId, 'value');
    } else if (sourceType === 'abp' || sourceType === 'accountBasedPensions') {
        remaining = reduceFromList(modified.assets?.accountBasedPensions, targetAssetId);
    } else if (sourceType === 'super' || sourceType === 'superannuation') {
        remaining = reduceFromList(modified.assets?.superannuation, targetAssetId, 'value');
    } else if (sourceType === 'termDeposits') {
        remaining = reduceFromList(modified.assets?.termDeposits, targetAssetId, 'value');
    }

    if (remaining > 0) {
        if (!modified.assets) modified.assets = {};
        if (!modified.assets.bankAccounts) {
            modified.assets.bankAccounts = [
                {
                    id: 'auto-bank-adjust',
                    type: 'bankAccounts',
                    description: 'Cash balance',
                    value: 0,
                    /* interest, not capital growth — see bankPlugin.ts */ incomeRate: 2, growthRate: 0,
                    ownership: 'joint',
                },
            ];
        }
        const first = modified.assets.bankAccounts[0]!;
        const current = first.value || 0;
        const reduction = Math.min(current, remaining);
        first.value = current - reduction;
        remaining -= reduction;
    }

    if (!modified.assets) modified.assets = {};
    if (!modified.assets.annuities) modified.assets.annuities = [];

    // Remove any existing annuities with the same ID to avoid duplicates when settings change.
    modified.assets.annuities = modified.assets.annuities.filter((a: any) => a.id !== annuityRecord.id);

    // One shape for every builder — see annuityAssetFromRecord. A fixed-term annuity's `value`
    // reduces as capital comes back; a lifetime one carries `assessableAsset` (60% → 30%).
    modified.assets.annuities.push(annuityAssetFromRecord(annuityRecord, { ownership: annuityRecord.owner || 'joint' }));

    return modified;
}

/**
 * Buy the annuity, ONCE, and report what each liquid asset gave up.
 *
 * OWNER-CAUGHT, 25 September 2026. The aged care analyser applied an annuity and then adjusted the
 * same balances again by hand, so a $190,000 annuity funded from sale proceeds cost the household
 * $380,000. One call, in one place, is the only arrangement that cannot drift: everything that
 * needs to know what the purchase consumed reads the answer from here rather than re-deriving it.
 *
 * The draw is measured, not assumed, so it stays right when the named account cannot cover the
 * purchase and `applyAnnuityToFactFind` takes the shortfall from the first bank account. The
 * funding picker adds these amounts back when it asks "what could this annuity be bought with",
 * because the money it is already using belongs in that answer.
 *
 * Keys are `assetType-assetId`, the same key the pickers use for their options.
 */
export function applyAnnuityWithDraw(
    factFind: FactFind,
    annuityRecord: any,
): { factFind: any; drawnFrom: Record<string, number> } {
    const before = snapshotLiquidBalances(factFind);
    const applied = applyAnnuityToFactFind(factFind, annuityRecord);
    return { factFind: applied, drawnFrom: diffLiquidBalances(before, snapshotLiquidBalances(applied)) };
}

/**
 * What an asset could put toward THIS annuity: its balance, plus whatever this annuity has already
 * taken from it, less anything the room deposit has claimed.
 *
 * The middle term is the one that matters (owner-caught, 25 September 2026). The picker reads the
 * balance sheet AFTER the annuity is bought, so without adding the draw back the list shrank as the
 * client typed: on a $194,713 proceeds account a $190,000 annuity left $4,713 on offer, and the row
 * vanished altogether once the balance hit nil.
 */
export function annuityFundingAvailable(assetValue: number, drawnByThisAnnuity: number, claimedByRad: number): number {
    return Math.max(0, numOr(assetValue, 0) + numOr(drawnByThisAnnuity, 0) - numOr(claimedByRad, 0));
}

/** Every liquid balance, keyed `assetType-assetId` — the key the funding pickers use. */
export function snapshotLiquidBalances(factFind: any): Record<string, number> {
    const out: Record<string, number> = {};
    for (const assetType of ASSET_TYPES.filter((t) => t.isLiquid).map((t) => t.id)) {
        for (const asset of (factFind?.assets?.[assetType] || []) as any[]) {
            out[`${assetType}-${asset?.id}`] = numOr(asset?.value ?? asset?.balance, 0);
        }
    }
    return out;
}

/** What each asset gave up between two snapshots. Reductions only; a cent of float noise is ignored. */
export function diffLiquidBalances(before: Record<string, number>, after: Record<string, number>): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [key, was] of Object.entries(before)) {
        const drawn = was - (after[key] ?? 0);
        if (drawn > 0.005) out[key] = drawn;
    }
    return out;
}

export function summariseAnnuityImpact(annuityRecord: any): any {
    if (!annuityRecord) return null;
    return {
        annuityType: annuityRecord.annuityType,
        annualPayment: annuityRecord.annualPayment,
        assessableIncome: annuityRecord.assessableIncome,
        assessableAsset: annuityRecord.assessableAsset,
        residualValue: annuityRecord.residualValue,
        fundingSourceLabel: annuityRecord.fundingSourceLabel,
        owner: annuityRecord.owner,
        // Where the rate came from, and — for the guaranteed shape — how the money split between the
        // annuity and the life policy. The screens show both; nothing else reads them.
        rateBasis: annuityRecord.rateBasis,
        rateExtrapolated: annuityRecord.rateExtrapolated,
        annuitySlice: annuityRecord.annuitySlice,
        insuranceSlice: annuityRecord.insuranceSlice,
        splitClamped: annuityRecord.splitClamped,
    };
}

/* ------------------------------------------------------------------------------------------------
 * TERM, DEFERRED AND EXISTING ANNUITIES — one reading of an annuity asset, shared by the means test,
 * the income walk, net worth and the projection's ageing plugin (F.7 of the actuarial review pack,
 * built 17 Sep 2026).
 *
 * WHY. Three builders wrote an annuity asset in three shapes, the client profile wrote a fourth
 * (`fixed_term`, `term`, `income`, `rcv`), and the assessment read only the strategy-built spelling.
 * A profile-entered annuity therefore had no payment in the income walk at all, and NO annuity's
 * payments reached the Age Pension income test — the assessment counted the asset and forgot the
 * income, which flattered every annuity strategy the engines proposed. Nothing aged an annuity across
 * a projection either: a fixed-term annuity kept its full purchase price on the books for ever, and
 * a lifetime annuity never stepped from 60% to 30% on its threshold day.
 *
 * THE RULES (SSA ss 1098–1099A; s 9(1) "asset-tested income stream (long term)"; Guide 4.9):
 *   - LIFETIME (commenced on/after 1 July 2019): asset 60% of purchase price to the threshold day,
 *     30% after (`lifetimeAnnuityAssetRate`); income 60% of the payments. A DEFERRED lifetime annuity
 *     is the same product with its payments starting later: same asset rule from the purchase day,
 *     and no income counted until the payments begin.
 *   - LONG-TERM fixed term (more than five years): asset = purchase price less the capital returned
 *     so far, never below the residual; income = payment less the deductible amount
 *     ((purchase − residual) ÷ term).
 *   - SHORT-TERM fixed term (five years or less): a financial investment — the same reducing value
 *     is DEEMED, and the payments themselves are not counted.
 *   - At the end of a fixed term the residual is returned as cash and the payments stop.
 * ---------------------------------------------------------------------------------------------- */

/** A fixed term at or under this many years is a short-term income stream: deemed, not deducted. */
export const SHORT_TERM_INCOME_STREAM_MAX_YEARS = 5;
/**
 * Indicative pricing only. A deferred lifetime annuity is priced here as an immediate one bought at
 * the age the payments start, on a purchase price the provider is assumed to have grown at this rate
 * through the deferral. Real quotes also carry a mortality credit this ignores — the indicative
 * figure is therefore on the low side, and a provider's quote (`incomeOverride`) always wins.
 */
export const DEFERRED_ANNUITY_ACCUMULATION_RATE = 0.04;

/**
 * `guaranteed` is the annuity-plus-life-policy shape. For the means test it behaves like a lifetime
 * annuity on its annuity half and like an ordinary asset on its insurance half; its death benefit
 * is the whole price and never moves. Nothing deems it and nothing matures.
 */
export type AnnuityMeansTestKind = 'lifetime' | 'guaranteed' | 'longTerm' | 'shortTerm';

export interface AnnuityTerms {
    kind: AnnuityMeansTestKind;
    /** What was paid for it — `purchasePrice`, or the value where no price was recorded. */
    purchaseAmount: number;
    /** What it pays THIS year: nil while a deferred annuity is still deferred, nil once a term has ended. */
    annualPayment: number;
    /**
     * The deductible amount: capital returned each year on a fixed term ((purchase − residual) ÷ term);
     * for a lifetime annuity (purchase − residual) ÷ life expectancy at commencement — the ATO's
     * tax-free component of each payment. The means test uses it for a long-term fixed annuity only.
     */
    deductionAmount: number;
    residualValue: number;
    termYears: number | null;
    deferralYears: number;
    /** Whole years held, as far as the asset can say. */
    yearsElapsed: number;
    /** Whether the fixed term has run its course (payments stopped, residual due back). */
    matured: boolean;
    /**
     * INFLATION-LINKED (15 Oct 2026): the payment rises each year at CPI — Challenger's indexed
     * Liquid Lifetime, UniSuper's Lifetime Income. Priced from the sheet's own indexed table, and
     * the projection grows the payment from the year payments start.
     */
    indexed: boolean;
    /**
     * A GUARANTEE PERIOD (lifetime only): if death comes within it, the remaining guaranteed
     * payments go to the estate as a lump sum — capped at what the capital access schedule allows.
     * UniSuper's Lifetime Income: the lesser of 10 years and life expectancy. 0 where there is none.
     */
    guaranteePeriodYears: number;
    /** The share that continues to a spouse on death, %, where recorded (null otherwise). Disclosed; no death is projected. */
    reversionPct: number | null;
}

const numOr = (v: unknown, fallback: number): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : fallback;
};

/**
 * Whole calendar years from `from` to `to` (anniversaries passed), never negative. Calendar
 * arithmetic, not milliseconds ÷ 365.25: a date exactly one year on is one year held, where the
 * millisecond route made 365 days come out at 0.999 and floored it to nothing.
 */
export function wholeYearsBetween(from: Date, to: Date): number {
    let years = to.getFullYear() - from.getFullYear();
    const beforeAnniversary = to.getMonth() < from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() < from.getDate());
    if (beforeAnniversary) years -= 1;
    return Math.max(0, years);
}

/** Whole years from a recorded commencement date to `asOf`; null where the date is unusable. */
export function annuityYearsSinceCommencement(asset: any, asOf: Date = new Date()): number | null {
    if (!asset?.commencementDate || asset?.commencementDateUnsure) return null;
    const text = String(asset.commencementDate);
    // A bare date is read as a LOCAL date, so it compares with the projection's local dates.
    const commenced = /^\d{4}-\d{2}-\d{2}$/.test(text) ? new Date(`${text}T00:00:00`) : new Date(text);
    if (Number.isNaN(commenced.getTime())) return null;
    return wholeYearsBetween(commenced, asOf);
}

/**
 * The one reading of an annuity asset, whichever builder or form wrote it. `ownerAgeNow` lets a
 * profile-entered lifetime annuity strike its deductible amount from life expectancy.
 */
export function annuityTermsOf(asset: any, asOf: Date = new Date(), ownerAgeNow: number | null = null): AnnuityTerms {
    const rawType = String(asset?.annuityType ?? '');
    const termYears = (() => {
        const t = numOr(asset?.termYears ?? asset?.term, 0);
        return t > 0 ? t : null;
    })();
    const isGuaranteed = rawType === 'guaranteed';
    const isFixed = !isGuaranteed && (rawType === 'fixedTerm' || rawType === 'fixed_term' || (rawType === '' && termYears != null && asset?.assessableAsset === undefined));
    const isLifetime = rawType === 'lifetime' || (rawType === '' && !isFixed);

    const value = Math.max(0, numOr(asset?.value, 0));
    const purchaseAmount = Math.max(0, numOr(asset?.purchasePrice, 0)) || value;
    // A guaranteed shape's estate value is its whole price, whatever a stale field says.
    const residualValue = isGuaranteed ? purchaseAmount : Math.max(0, numOr(asset?.residualValue ?? asset?.rcv, 0));
    const deferralYears = Math.max(0, numOr(asset?.deferralYears, 0));
    const yearsElapsed = asset?.__annuityYearsElapsed != null
        ? Math.max(0, numOr(asset.__annuityYearsElapsed, 0))
        : (annuityYearsSinceCommencement(asset, asOf) ?? 0);

    let kind: AnnuityMeansTestKind;
    if (isGuaranteed) kind = 'guaranteed';
    else if (isLifetime) kind = 'lifetime';
    else if (termYears != null && termYears <= SHORT_TERM_INCOME_STREAM_MAX_YEARS) kind = 'shortTerm';
    else kind = 'longTerm';

    const matured = (kind === 'longTerm' || kind === 'shortTerm') && termYears != null && yearsElapsed >= termYears;
    const indexed = kind === 'lifetime' && (asset?.indexed === true || asset?.indexation === true);
    const guaranteePeriodYears = kind === 'lifetime' ? Math.max(0, numOr(asset?.guaranteePeriodYears, 0)) : 0;
    const reversionRaw = numOr(asset?.reversionPct, -1);
    const reversionPct = reversionRaw >= 0 ? Math.min(100, reversionRaw) : null;

    // The deductible amount: recorded by a builder, else struck from the terms.
    let deductionAmount = 0;
    const recorded = numOr(asset?.deductionAmount, -1);
    if (recorded >= 0) {
        deductionAmount = recorded;
    } else if (kind === 'guaranteed') {
        // The tax-free component of each payment runs on the ANNUITY half, which returns nothing
        // of its own price (the policy does that), over life expectancy at commencement.
        const ageAtCommencement = asset?.__ageAtCommencement != null
            ? numOr(asset.__ageAtCommencement, 0)
            : (ownerAgeNow != null ? ownerAgeNow - yearsElapsed : null);
        const annuitySlice = Math.max(0, numOr(asset?.annuitySlice, 0)) || purchaseAmount * carePlusSplitFor(ageAtCommencement ?? 85, sexOf(asset?.__ownerSex)).annuity;
        if (ageAtCommencement != null && ageAtCommencement > 0) {
            const lifeExpectancy = getLifeExpectancy({ age: ageAtCommencement, partnerAge: null, type: 'single', sex: sexOf(asset?.__ownerSex) });
            deductionAmount = lifeExpectancy > 0 ? annuitySlice / lifeExpectancy : 0;
        }
    } else if (kind !== 'lifetime') {
        deductionAmount = termYears ? Math.max(0, purchaseAmount - residualValue) / termYears : 0;
    } else {
        const ageAtCommencement = asset?.__ageAtCommencement != null
            ? numOr(asset.__ageAtCommencement, 0)
            : (ownerAgeNow != null ? ownerAgeNow - yearsElapsed : null);
        if (ageAtCommencement != null && ageAtCommencement > 0) {
            const lifeExpectancy = getLifeExpectancy({ age: ageAtCommencement, partnerAge: null, type: 'single' });
            deductionAmount = lifeExpectancy > 0 ? Math.max(0, purchaseAmount - residualValue) / lifeExpectancy : 0;
        }
    }

    const statedPayment = Math.max(0, numOr(asset?.annualPayment ?? asset?.income, 0));
    let annualPayment = statedPayment;
    if (kind === 'lifetime' && deferralYears > 0) {
        annualPayment = yearsElapsed >= deferralYears ? Math.max(0, numOr(asset?.deferredAnnualPayment, statedPayment)) : 0;
    }
    if (matured) annualPayment = 0;

    return { kind, purchaseAmount, annualPayment, deductionAmount, residualValue, termYears, deferralYears, yearsElapsed, matured, indexed, guaranteePeriodYears, reversionPct };
}

/**
 * What the Age Pension (and aged-care) income test counts from this annuity this year.
 * A stated `assessableIncome` — a builder's figure or an adviser's override — is honoured for a
 * lifetime or long-term annuity; a short-term one is deemed instead and counts nothing here.
 */
export function annuityAssessableIncomeOf(asset: any, asOf: Date = new Date()): number {
    const terms = annuityTermsOf(asset, asOf);
    if (terms.kind === 'shortTerm' || terms.matured) return 0;
    if (terms.kind === 'guaranteed') {
        // All of its payments come from the annuity half: 60% counted, no deferral to wait out.
        const statedNum = numOr(asset?.assessableIncome, -1);
        return statedNum >= 0 && terms.annualPayment > 0 ? statedNum : terms.annualPayment * 0.6;
    }
    if (terms.kind === 'lifetime') {
        if (terms.deferralYears > 0 && terms.yearsElapsed < terms.deferralYears) return 0;
        const stated = terms.deferralYears > 0 ? asset?.deferredAssessableIncome : asset?.assessableIncome;
        const statedNum = numOr(stated, -1);
        return statedNum >= 0 && terms.annualPayment > 0 ? statedNum : terms.annualPayment * 0.6;
    }
    const statedNum = numOr(asset?.assessableIncome, -1);
    if (statedNum >= 0) return statedNum;
    return Math.max(0, terms.annualPayment - terms.deductionAmount);
}

/**
 * What the assets test counts. A lifetime annuity carries `assessableAsset` once built or aged; an
 * existing one entered on the profile is struck from the owner's age and how long it has been held.
 * A fixed-term annuity counts at its current (reducing) value.
 */
export function annuityAssessableAssetOf(asset: any, ownerAgeNow: number | null | undefined, asOf: Date = new Date()): number {
    const terms = annuityTermsOf(asset, asOf);
    if (terms.kind === 'longTerm' || terms.kind === 'shortTerm') return Math.max(0, numOr(asset?.value, 0));
    if (asset?.assessableAsset !== undefined && asset?.assessableAsset !== null) return Math.max(0, numOr(asset.assessableAsset, 0));
    const ageAtCommencement = asset?.__ageAtCommencement != null
        ? numOr(asset.__ageAtCommencement, 0)
        : (ownerAgeNow != null ? ownerAgeNow - terms.yearsElapsed : null);
    if (terms.kind === 'guaranteed') {
        // Reconstruct the two halves where a stored figure is missing: annuity half at 60%/30%,
        // insurance half at the premium paid.
        const split = carePlusSplitFor(ageAtCommencement ?? 85, sexOf(asset?.__ownerSex));
        const annuitySlice = Math.max(0, numOr(asset?.annuitySlice, 0)) || terms.purchaseAmount * split.annuity;
        const insuranceSlice = Math.max(0, numOr(asset?.insuranceSlice, 0)) || terms.purchaseAmount * split.insurance;
        return annuitySlice * lifetimeAnnuityAssetRate(ageAtCommencement, terms.yearsElapsed) + insuranceSlice;
    }
    return terms.purchaseAmount * lifetimeAnnuityAssetRate(ageAtCommencement, terms.yearsElapsed);
}

/**
 * Advance an annuity to `yearsElapsed` years held — mutates the asset. Returns whether a fixed term
 * has ended this year (the caller returns the residual to cash and drops the asset).
 */
export function ageAnnuityAsset(
    asset: any,
    { yearsElapsed, ownerAgeNow, partnerAgeNow = null, ownerSex = null, partnerSex = null, indexationPct = 0 }: {
        yearsElapsed: number;
        ownerAgeNow: number | null;
        partnerAgeNow?: number | null;
        ownerSex?: Sex | null;
        partnerSex?: Sex | null;
        /** The CPI assumption an inflation-linked payment rises at, % a year (the run's). */
        indexationPct?: number;
    },
): { matured: boolean; residualPaid: number } {
    asset.__annuityYearsElapsed = Math.max(0, yearsElapsed);
    const terms = annuityTermsOf(asset);

    if (terms.kind === 'guaranteed') {
        /*
         * NOTHING TAPERS, ONE THING STEPS. The death benefit is the whole price for life, so the
         * residual is pinned to it every year regardless of what the record carried in. The only
         * moving part is the annuity half's assets-test rate, which steps from 60% to 30% on the
         * owner's threshold day exactly as a plain lifetime annuity's does; the insurance half stays
         * at the premium paid. No deferral, no maturity, no capital access schedule.
         */
        const elapsed = terms.yearsElapsed;
        const ageAtCommencement = asset.__ageAtCommencement != null
            ? numOr(asset.__ageAtCommencement, 0)
            : (ownerAgeNow != null ? ownerAgeNow - elapsed : null);
        if (asset.__ageAtCommencement == null && ageAtCommencement != null) asset.__ageAtCommencement = ageAtCommencement;
        if (asset.__ownerSex == null && ownerSex) asset.__ownerSex = ownerSex;
        const purchase = terms.purchaseAmount;
        const split = carePlusSplitFor(ageAtCommencement ?? 85, sexOf(asset.__ownerSex));
        if (asset.annuitySlice == null) asset.annuitySlice = roundCurrency(purchase * split.annuity);
        if (asset.insuranceSlice == null) asset.insuranceSlice = roundCurrency(purchase * split.insurance);
        asset.residualCapital = 1;
        asset.residualValue = roundCurrency(purchase);
        asset.assessableAsset = roundCurrency(
            numOr(asset.annuitySlice, 0) * lifetimeAnnuityAssetRate(ageAtCommencement, elapsed) + numOr(asset.insuranceSlice, 0)
        );
        return { matured: false, residualPaid: 0 };
    }

    if (terms.kind === 'lifetime') {
        const elapsed = terms.yearsElapsed;
        const ageAtCommencement = asset.__ageAtCommencement != null
            ? numOr(asset.__ageAtCommencement, 0)
            : (ownerAgeNow != null ? ownerAgeNow - elapsed : null);
        if (asset.__ageAtCommencement == null && ageAtCommencement != null) asset.__ageAtCommencement = ageAtCommencement;
        const purchase = terms.purchaseAmount;

        /*
         * THE DEATH BENEFIT FOLLOWS THE CAPITAL ACCESS SCHEDULE — see capitalAccessDeathBenefitFactor.
         * The initial share is what the builder recorded (or, for an annuity entered on the profile,
         * what its residual was when first seen); the period is the one stamped at purchase, else
         * struck from the age at commencement and the owner's sex (unknown = the shorter table).
         */
        if (asset.residualCapital == null && purchase > 0) asset.residualCapital = Math.min(1, Math.max(0, terms.residualValue / purchase));
        if (asset.__ownerSex == null && ownerSex) asset.__ownerSex = ownerSex;
        if (asset.capitalAccessPeriodYears == null && ageAtCommencement != null && ageAtCommencement > 0) {
            const joint = asset.ownership === 'joint' || asset.reversionary === true;
            const partnerAtCommencement = joint && partnerAgeNow != null ? partnerAgeNow - elapsed : null;
            asset.capitalAccessPeriodYears = capitalAccessPeriodYears(getLifeExpectancy({
                age: ageAtCommencement,
                partnerAge: partnerAtCommencement,
                type: partnerAtCommencement != null ? 'joint' : 'single',
                sex: sexOf(asset.__ownerSex),
                partnerSex: partnerSex,
            }));
        }
        if (asset.capitalAccessPeriodYears != null) {
            asset.residualValue = roundCurrency(purchase * numOr(asset.residualCapital, 0) * capitalAccessDeathBenefitFactor(elapsed, numOr(asset.capitalAccessPeriodYears, 0)));
        }
        if (asset.ownership === 'joint' && partnerAgeNow != null) {
            const partnerAtCommencement = asset.__partnerAgeAtCommencement != null ? numOr(asset.__partnerAgeAtCommencement, 0) : partnerAgeNow - elapsed;
            if (asset.__partnerAgeAtCommencement == null) asset.__partnerAgeAtCommencement = partnerAtCommencement;
            asset.assessableAsset = (purchase / 2) * lifetimeAnnuityAssetRate(ageAtCommencement, elapsed)
                + (purchase / 2) * lifetimeAnnuityAssetRate(partnerAtCommencement, elapsed);
        } else {
            asset.assessableAsset = purchase * lifetimeAnnuityAssetRate(ageAtCommencement, elapsed);
        }
        if (terms.deferralYears > 0) {
            const started = elapsed >= terms.deferralYears;
            asset.annualPayment = started ? Math.max(0, numOr(asset.deferredAnnualPayment, 0)) : 0;
            asset.assessableIncome = started ? Math.max(0, numOr(asset.deferredAssessableIncome, asset.annualPayment * 0.6)) : 0;
        }
        /*
         * INFLATION-LINKED (15 Oct 2026): the payment rises at CPI each year from the year payments
         * start, off the first-year payment stamped the first time the asset is aged (a profile-entered
         * annuity's `income`, a builder's `annualPayment`). 60% of the risen payment counts as income.
         */
        if (terms.indexed) {
            const started = elapsed >= terms.deferralYears;
            if (started) {
                if (asset.__annuityBasePayment == null) {
                    asset.__annuityBasePayment = Math.max(0, numOr(asset.annualPayment ?? asset.income, 0));
                }
                const base = numOr(asset.__annuityBasePayment, 0);
                const yearsPaying = Math.max(0, elapsed - terms.deferralYears);
                asset.annualPayment = roundCurrency(base * Math.pow(1 + Math.max(0, indexationPct) / 100, yearsPaying));
                asset.assessableIncome = roundCurrency(asset.annualPayment * 0.6);
            }
        }
        /*
         * A GUARANTEE PERIOD (15 Oct 2026): within it the estate receives the remaining guaranteed
         * payments as a lump sum, never more than the capital access schedule's death-benefit ceiling
         * for that year. Beside a residual the schedule already gives, the larger stands.
         */
        if (terms.guaranteePeriodYears > 0) {
            const paying = Math.max(0, numOr(asset.annualPayment ?? terms.annualPayment, 0));
            const remaining = Math.max(0, terms.guaranteePeriodYears - elapsed) * paying;
            const ceiling = asset.capitalAccessPeriodYears != null
                ? purchase * capitalAccessDeathBenefitFactor(elapsed, numOr(asset.capitalAccessPeriodYears, 0))
                : purchase;
            const guaranteed = roundCurrency(Math.min(remaining, ceiling));
            asset.residualValue = Math.max(numOr(asset.residualValue, 0), guaranteed);
        }
        return { matured: false, residualPaid: 0 };
    }

    // Fixed term: capital comes back through the payments, so the counted value falls to the residual.
    if (terms.matured) {
        asset.value = 0;
        asset.annualPayment = 0;
        asset.assessableIncome = 0;
        return { matured: true, residualPaid: terms.residualValue };
    }
    asset.value = Math.max(terms.residualValue, terms.purchaseAmount - terms.deductionAmount * terms.yearsElapsed);
    return { matured: false, residualPaid: 0 };
}

/** What net worth carries for an annuity: a fixed term at the capital still to come back, a lifetime one at its residual. */
export function annuityNetWorthValueOf(asset: any): number {
    const terms = annuityTermsOf(asset);
    // The guaranteed shape is worth its whole price to the estate, always.
    if (terms.kind === 'guaranteed') return Math.max(0, terms.purchaseAmount);
    if (terms.kind !== 'lifetime') return Math.max(0, numOr(asset?.value, 0));
    return asset?.residualValue !== undefined ? Math.max(0, numOr(asset.residualValue, 0)) : Math.max(0, numOr(asset?.value, 0));
}

/**
 * The ONE shape every builder writes an annuity asset in. `record` is what `buildAnnuityImpact`
 * returned; the overrides name it. Carries the purchase price, the deferral terms and the age at
 * commencement so the ageing plugin and the means test can read it back years later.
 */
export function annuityAssetFromRecord(
    record: any,
    overrides: { id?: string; description?: string; ownership?: string } = {},
): any {
    const isLifetime = record.annuityType === 'lifetime';
    const isGuaranteed = record.annuityType === 'guaranteed';
    const deferralYears = Math.max(0, numOr(record.deferralYears, 0));
    const kindLabel = isGuaranteed ? 'Guaranteed' : isLifetime ? (deferralYears > 0 ? 'Deferred lifetime' : 'Lifetime') : 'Fixed-term';
    const asset: any = {
        id: overrides.id ?? record.id,
        type: 'annuities',
        description: overrides.description ?? `${kindLabel} annuity`,
        value: record.purchaseAmount,
        purchasePrice: record.purchaseAmount,
        ownership: overrides.ownership ?? record.owner ?? 'client',
        annualPayment: record.annualPayment,
        assessableIncome: record.assessableIncome,
        residualValue: record.residualValue,
        deductionAmount: record.deductionAmount,
        termYears: record.termYears || null,
        annuityType: record.annuityType,
        __annuityYearsElapsed: 0,
    };
    if (record.age != null) asset.__ageAtCommencement = record.age;
    if (record.partnerAge != null) asset.__partnerAgeAtCommencement = record.partnerAge;
    if (isGuaranteed) {
        asset.assessableAsset = record.assessableAsset;
        asset.residualCapital = 1;
        asset.annuitySlice = record.annuitySlice;
        asset.insuranceSlice = record.insuranceSlice;
        if (record.ownerSex) asset.__ownerSex = record.ownerSex;
    }
    if (isLifetime) {
        asset.assessableAsset = record.assessableAsset;
        asset.residualCapital = Math.min(1, Math.max(0, numOr(record.residualCapital, 0)));
        if (record.capitalAccessPeriodYears != null) asset.capitalAccessPeriodYears = numOr(record.capitalAccessPeriodYears, 0) || null;
        if (record.lifeExpectancySexKnown != null) asset.lifeExpectancySexKnown = !!record.lifeExpectancySexKnown;
        if (record.ownerSex) asset.__ownerSex = record.ownerSex;
        if (deferralYears > 0) {
            asset.deferralYears = deferralYears;
            asset.deferredAnnualPayment = record.deferredAnnualPayment;
            asset.deferredAssessableIncome = record.deferredAssessableIncome;
        }
        // The inflation-linked shape and a guarantee period, so the ageing plugin grows the payment
        // and the estate carries the guarantee (15 Oct 2026).
        if (record.indexation) asset.indexed = true;
        if (numOr(record.guaranteePeriodYears, 0) > 0) asset.guaranteePeriodYears = numOr(record.guaranteePeriodYears, 0);
        if (numOr(record.reversionPct, -1) >= 0) asset.reversionPct = numOr(record.reversionPct, 0);
    }
    return asset;
}
